import {
  Breakpoint,
  Button,
  ButtonSize,
  ButtonTheme,
  Color,
  FontWeight,
  IconName,
  Markdown,
  QuizzAnswerIconType,
  SimpleMarkdown,
  TextButton,
  TextButtonTheme,
  Typography,
  TypographyElement,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useContext, useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { PageDataContext } from '../../components/page';
import { PageData, Statement } from '../../interfaces';

import {
  BottomWrapper,
  Container,
  DescriptionContainer,
  HeaderContainer,
  Statements,
  StatementsWrapper,
  StatementWrapper,
  StyledHeaderTypography,
  StyledQuizAnswer,
  StyledTypography,
  StyledVideoSection,
  TopWrapper,
  VideoWrapper,
} from './animationsWithStatementsChoice.styled';
import { AnimationsWithStatementChoiceProps } from './animationsWithStatementsChoice.types';
import { ReactComponent as ALetter } from './images/A.svg';
import { ReactComponent as BLetter } from './images/B.svg';

export const AnimationsWithStatementChoice: React.FC<AnimationsWithStatementChoiceProps> = (
  props: AnimationsWithStatementChoiceProps
): JSX.Element => {
  const { callComplete, completed, content, baseUrl }: AnimationsWithStatementChoiceProps = props;
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const {
    slidesOverallCounter,
    currentSlideCounter,
    title,
    description,
  }: Partial<PageData> = dataCtx ?? {};
  const isSmallDesktop: boolean = useMediaQuery({ query: Breakpoint.SmallDesktop });
  const { t }: TransProps<never> = useTranslation();
  const [currentAnimationIndex, setCurrentAnimationIndex]: UseState<number> = useState<number>(0);
  const [showStatements, setShowStatements]: UseState<boolean> = useState<boolean>(false);
  const [selectedStatements, setSelectedStatements]: UseState<string[]> = useState<string[]>([]);
  const isCurrentAnimationLast: boolean = currentAnimationIndex === content.length - 1;

  const handleNextAnimation: () => void = (): void => {
    setShowStatements(false);
    setSelectedStatements([]);
    setCurrentAnimationIndex(currentAnimationIndex + 1);
  };

  const handleEndOfVideo: () => void = (): void => {
    if (content[currentAnimationIndex].statements?.length) {
      setShowStatements(true);
    } else if (!completed) {
      callComplete();
    }
  };

  useEffect((): void => {
    if (
      !completed
      && isCurrentAnimationLast
      && !!content[currentAnimationIndex].statements?.some(
        (statement: Statement): boolean => selectedStatements.includes(statement.text) && !!statement.isCorrect
      )
    ) {
      callComplete();
    }
  }, [completed, currentAnimationIndex, selectedStatements, content, callComplete, isCurrentAnimationLast]);

  return (
    <Container>
      <HeaderContainer>
        <StyledHeaderTypography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextSM}>
          <Markdown
            text={t(
              'competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.slides',
              { currentSlideCounter, slidesOverallCounter }
            )}
          />
        </StyledHeaderTypography>
        <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayXS}>
          {title}
        </Typography>
      </HeaderContainer>
      {!!description && (
        <DescriptionContainer>
          <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
            <Markdown text={description} />
          </Typography>
        </DescriptionContainer>
      )}
      {showStatements ? (
        <StatementsWrapper>
          <TopWrapper>
            <TextButton
              icon={IconName.ChevronLeft}
              label={t('competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.watchAgain')}
              onClick={(): void => setShowStatements(false)}
              variant={TextButtonTheme.TertiaryGrey}
            />
            <StyledTypography
              fontWeight={FontWeight.Bold}
              variant={isSmallDesktop ? TypographyVariant.DisplaySM : TypographyVariant.TextLG}
            >
              {content[currentAnimationIndex].question
                ?? t('competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.question')
              }
            </StyledTypography>
          </TopWrapper>
          <Statements>
            {content[currentAnimationIndex].statements?.map((statement: Statement, index: number): JSX.Element => (
              <StatementWrapper key={statement.text}>
                {index === 0 ? (
                  <ALetter color={selectedStatements.includes(statement.text)
                    ? statement.isCorrect
                      ? Color.Green900
                      : Color.Red900
                    : Color.LightTurquoise
                  }
                  />
                ) : (
                  <BLetter color={selectedStatements.includes(statement.text)
                    ? statement.isCorrect
                      ? Color.Green900
                      : Color.Red900
                    : Color.LightTurquoise
                  }
                  />
                )}
                <StyledTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
                  <SimpleMarkdown text={statement.text} />
                </StyledTypography>
                {!selectedStatements?.includes(statement.text) ? (
                  <Button
                    disabled={!!selectedStatements?.length && !statement.isCorrect}
                    fullWidth={!isSmallDesktop}
                    label={t('competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.choose')}
                    onClick={(): void => setSelectedStatements([...selectedStatements, statement.text])}
                    size={ButtonSize.Large}
                    variant={ButtonTheme.SecondaryBrand}
                  />
                ) : (
                  <BottomWrapper>
                    <StyledQuizAnswer
                      content={statement.answerExplanation ?? t(statement.isCorrect
                        ? 'competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.correctAnswer'
                        : 'competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.incorrectAnswer'
                      )}
                      iconType={statement.isCorrect ? QuizzAnswerIconType.Correct : QuizzAnswerIconType.Incorrect}
                      withBackgroundColor
                    />
                    {!!statement.isCorrect && !isCurrentAnimationLast && (
                      <Button
                        fullWidth={!isSmallDesktop}
                        label={t('competence.app.scrollLesson.componentsV2.animationsWithStatementsChoice.continue')}
                        onClick={handleNextAnimation}
                        size={ButtonSize.Large}
                        variant={ButtonTheme.SecondaryBrand}
                      />
                    )}
                  </BottomWrapper>
                )}
              </StatementWrapper>
            ))}
          </Statements>
        </StatementsWrapper>
      ) : (
        <VideoWrapper>
          <StyledVideoSection
            callComplete={handleEndOfVideo}
            isFinishScreenDisabled
            isScrollContent
            isScrollLesson
            videoSrc={baseUrl + content[currentAnimationIndex].videoSrc}
          />
        </VideoWrapper>
      )}
    </Container>
  );
};
